/**
 * ATC Click Tracker
 *
 * Logs successful "Add to Cart" actions on the product page, from:
 *   - the main product form button ("main")
 *   - the sticky add-to-cart bar button ("sticky")
 * Same records as the live theme. Quick view / quick shop / product-card buttons are not logged.
 *
 * Gecko adds to cart with fetch('/cart/add.js') instead of submitting the form (FormData from the
 * main form, JSON `{ items: [{ id, quantity }] }` from the sticky bar), so the tracker wraps fetch,
 * logs only when that request succeeds, and uses the last add-to-cart button the customer clicked to
 * tell main from sticky. If AJAX add-to-cart is turned off, the main form's normal submit is logged.
 *
 * Each record is sent as JSON to WEBHOOK_URL (Google Apps Script endpoint, set in layout/theme.liquid).
 *
 * Log record format:
 * { date, time, button, product_id, product_title, variant_id, quantity, ip, country, state, city, url, user_agent }
 */

const WEBHOOK_URL = window.__ATC_TRACKER_WEBHOOK__ || '';

(function () {
  const MAIN_SECTION = '.t4s-section-main-product';
  const MAIN_FORM = 'form[data-type="add-to-cart-form"]:not([data-groups-pr-form])';
  const POPUPS = '.t4s-product-quick-view, .t4s-product-quick-shop';
  const STICKY_BAR = '.t4s-sticky-atc';
  const CLICK_WINDOW_MS = 15000;

  // { button: 'main' | 'sticky', productId, at }
  let lastClick = null;

  function getTimestamp() {
    const now = new Date();
    return {
      date: now.toISOString().slice(0, 10),
      time: now.toISOString().slice(11, 19) + ' UTC',
    };
  }

  async function getGeoInfo() {
    // Primary: ipinfo.io (50k req/month free, CORS-safe)
    try {
      const res = await fetch('https://ipinfo.io/json', { cache: 'no-store' });
      const json = await res.json();
      if (json.ip) {
        return {
          ip: json.ip,
          country: json.country || 'unknown',  // e.g. "US"
          state: json.region || 'unknown',      // e.g. "Texas"
          city: json.city || 'unknown',
        };
      }
    } catch { /* fall through */ }

    // Fallback: ipify for IP only
    try {
      const res = await fetch('https://api.ipify.org?format=json', { cache: 'no-store' });
      const json = await res.json();
      return { ip: json.ip || 'unknown', country: 'unknown', state: 'unknown', city: 'unknown' };
    } catch {
      return { ip: 'unknown', country: 'unknown', state: 'unknown', city: 'unknown' };
    }
  }

  async function sendLog(payload) {
    if (!WEBHOOK_URL) {
      console.log('[ATC Tracker]', payload);
      return;
    }
    try {
      await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        mode: 'no-cors',
        keepalive: true,
      });
    } catch (err) {
      console.warn('[ATC Tracker] Failed to send log:', err);
    }
  }

  async function log({ button, productId, variantId, quantity }) {
    const { date, time } = getTimestamp();
    const geo = await getGeoInfo();

    sendLog({
      date,
      time,
      button,
      product_id: productId || '',
      product_title: document.title.replace(' – GingerglowGift', '').trim(),
      variant_id: variantId || '',
      quantity: quantity || '1',
      ip: geo.ip,
      country: geo.country,
      state: geo.state,
      city: geo.city,
      url: window.location.href,
      user_agent: navigator.userAgent,
    });
  }

  function mainProductId() {
    return document.querySelector(`${MAIN_SECTION} ${MAIN_FORM}`)?.dataset.productid || '';
  }

  // Which add-to-cart button did the customer press? Only real clicks count (Gecko re-fires some itself).
  function sourceOf(button) {
    if (button.closest(STICKY_BAR)) return 'sticky';
    if (button.closest(POPUPS)) return null;
    const form = button.closest(MAIN_FORM);
    return form && form.closest(MAIN_SECTION) ? 'main' : null;
  }

  document.addEventListener('click', (event) => {
    if (!event.isTrusted) return;
    const button = event.target.closest?.('[data-atc-form], [data-action-atc]');
    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
    const source = sourceOf(button);
    lastClick = source ? { button: source, productId: mainProductId(), at: Date.now() } : null;
  }, true);

  // Variant id + quantity from Gecko's request body (FormData from the form, JSON from the sticky bar).
  function readItem(body) {
    try {
      if (body instanceof FormData) {
        return { variantId: body.get('id'), quantity: body.get('quantity') };
      }
      if (typeof body === 'string') {
        const json = JSON.parse(body);
        const item = json.items?.[0] || json;
        return { variantId: item.id, quantity: item.quantity };
      }
    } catch { /* not a body we know */ }
    return null;
  }

  const originalFetch = window.fetch;
  window.fetch = function (input, init) {
    const request = originalFetch.apply(this, arguments);
    try {
      const url = typeof input === 'string' ? input : input?.url || '';
      const click = lastClick;
      if (/\/cart\/add(\.js)?(\?|$)/.test(url) && click && Date.now() - click.at < CLICK_WINDOW_MS) {
        const item = readItem(init?.body);
        lastClick = null;
        if (item) {
          request.then((response) => {
            if (response.ok) {
              log({ button: click.button, productId: click.productId, variantId: item.variantId, quantity: String(item.quantity || 1) });
            }
          }).catch(() => {});
        }
      }
    } catch { /* never break the store's own fetch */ }
    return request;
  };

  // AJAX add-to-cart turned off: the main form submits normally.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form.matches?.(MAIN_FORM) || !form.closest(MAIN_SECTION)) return;
    log({
      button: 'main',
      productId: form.dataset.productid,
      variantId: form.querySelector('input[name="id"]')?.value,
      quantity: form.querySelector('input[name="quantity"]')?.value || '1',
    });
  });
})();
